/**
 * @file pairing.ts
 * @description VT2 ULTRA V2 USB 双插配对（用户侧，不含产测改 PID）。
 *
 * ## 分层
 * - 公开 API：`features/pairing/contract.ts` → `start()`
 * - 本文件：找 peer、按 manifest 打开结果通道、时序与监听
 * - 命令发送：device `enterMousePairingMode` / `enterDonglePairingMode`
 * - 报文编解码：`protocols/vision-ap-mouse.ts`
 * - 结果通道：只按 manifest `filters[].type: 'pairing'` 打开；usagePage/usage 不在协议层复写
 *
 * ## start() 时序
 * ```
 * ① 解析有线鼠标 + 接收器
 * ② openChannel({ usageType: 'pairing' }) 打开 manifest 声明的结果通道
 * ③ 有线发 04 00 00 B1（Cs=0）
 * ④ delay 200ms → 开始听 03 FF …
 * ⑤ delay 50ms → 接收器发 04 00 00 92（Cs=0）
 * ⑥ 等终态：FF FF 成功 / FF FE 超时 / ~61s 主机超时
 * ```
 *
 * @see docs/vt2-ultra-v2-usb-pairing-flow.md
 */

import type { HidChannel, HidReportListener } from '../../../../channels/hid/HidChannel'
import type { Telink3228CMouseDevice } from '../../../../devices/mouse/telink-3228c/device'
import { DeviceError, DeviceErrorCode } from '../../../../errors/DeviceError'
import { pairingFeature } from '../../../../features/pairing/contract'
import { RapooDeviceManager } from '../../../../manager/RapooDeviceManager'
import { ConnectionType } from '../../../../types/connection'
import { delay } from '../../../../utils/TimeUtils'
import {
  parseVisionApDonglePairingReport,
  VISION_AP_MOUSE_HID,
} from '../../protocols/vision-ap-mouse'

/** manifest `filters[].type`，对应接收器配对结果口。 */
const PAIRING_RESULT_CHANNEL = { usageType: 'pairing' as const }

/**
 * USB 双插配对 feature。
 * 从有线或无线任一端调用均可；内部解析 mouse / receiver。
 */
export class Telink3228CMousePairing extends pairingFeature {
  private static readonly pairingTimeoutMs = 61_000
  private static readonly afterMousePairCommandMs = 200
  private static readonly beforeDonglePairCommandMs = 50

  private get mouseDevice(): Telink3228CMouseDevice {
    return this.device as Telink3228CMouseDevice
  }

  /**
   * 正式配对入口。
   * @returns `true` 收到 `03 FF FF`；`false` 收到 `03 FF FE` 或主机超时
   */
  public async start(): Promise<boolean> {
    const { mouse, receiver } = this.resolveUsbPairingEndpoints()
    const result = await this.openDonglePairingResultChannel(receiver)

    try {
      await mouse.enterMousePairingMode()
      await delay(Telink3228CMousePairing.afterMousePairCommandMs)

      const listening = this.listenDonglePairingReports(result.channel)
      try {
        await delay(Telink3228CMousePairing.beforeDonglePairCommandMs)
        await receiver.enterDonglePairingMode()
        return await listening.wait
      } finally {
        listening.dispose()
      }
    } finally {
      await result.release()
    }
  }

  /** 单测可 override，避免 mock 全局 Manager。 */
  protected listModelDevices(model: string): Telink3228CMouseDevice[] {
    return RapooDeviceManager.getInstance().getDevices<Telink3228CMouseDevice>(model)
  }

  private resolveUsbPairingEndpoints(): {
    mouse: Telink3228CMouseDevice
    receiver: Telink3228CMouseDevice
  } {
    const self = this.mouseDevice
    const peer = this.findUsbPairingPeer(self)
    if (!peer) {
      throw new DeviceError(
        DeviceErrorCode.NOT_SUPPORTED,
        'USB 双插配对需要同时连接同型号有线鼠标与接收器',
      )
    }

    const selfType = self.getConnectionType()
    const peerType = peer.getConnectionType()
    if (selfType === ConnectionType.Wired && peerType === ConnectionType.Wireless) {
      return { mouse: self, receiver: peer }
    }
    if (selfType === ConnectionType.Wireless && peerType === ConnectionType.Wired) {
      return { mouse: peer, receiver: self }
    }

    throw new DeviceError(
      DeviceErrorCode.NOT_SUPPORTED,
      'USB 双插配对要求一端为有线鼠标、另一端为接收器',
    )
  }

  private findUsbPairingPeer(self: Telink3228CMouseDevice): Telink3228CMouseDevice | undefined {
    return this.listModelDevices(self.manifest.model).find((candidate) => {
      if (candidate === self) return false
      const selfType = self.getConnectionType()
      const peerType = candidate.getConnectionType()
      return (
        (selfType === ConnectionType.Wired && peerType === ConnectionType.Wireless) ||
        (selfType === ConnectionType.Wireless && peerType === ConnectionType.Wired)
      )
    })
  }

  /**
   * 按 manifest `type: 'pairing'` 打开接收器结果通道。
   * 经 `session.channels.openChannel`；DeviceChannelOpener 按 usagePage+usage 精确匹配。
   * 与控制口同 native 时 unregister 并回退控制口，避免 close 掐断主通道。
   */
  private async openDonglePairingResultChannel(receiver: Telink3228CMouseDevice): Promise<{
    channel: HidChannel
    release: () => Promise<void>
  }> {
    const preferred = await receiver.session.channels.openChannel(PAIRING_RESULT_CHANNEL)
    if (preferred && preferred !== receiver.channel) {
      const sharesPrimaryNative =
        Boolean(preferred.nativeDevice) && preferred.nativeDevice === receiver.channel.nativeDevice
      if (sharesPrimaryNative) {
        console.warn(
          '[Telink3228CMousePairing] pairing 通道与控制口共用 native，仅 unregister，回退控制口',
        )
        receiver.session.channels.unregister(PAIRING_RESULT_CHANNEL)
      } else {
        return {
          channel: preferred,
          release: async () => {
            await receiver.session.channels.close(PAIRING_RESULT_CHANNEL)
          },
        }
      }
    }

    console.warn(
      '[Telink3228CMousePairing] 未打开 manifest pairing 结果通道，回退控制口（可能听不到 03 上报）',
    )
    return {
      channel: receiver.channel,
      release: async () => undefined,
    }
  }

  /**
   * 订阅配对主动上报。
   * - `03 FF FD`：进窗，仅日志
   * - `03 FF FF`：成功
   * - `03 FF FE`：设备侧超时
   */
  private listenDonglePairingReports(channel: HidChannel): {
    wait: Promise<boolean>
    dispose: () => void
  } {
    let settled = false
    let timer: ReturnType<typeof setTimeout> | undefined
    let listener: HidReportListener | undefined
    let resolveWait: ((result: boolean) => void) | undefined

    const finish = (result: boolean) => {
      if (settled) return
      settled = true
      if (timer !== undefined) {
        clearTimeout(timer)
        timer = undefined
      }
      if (listener) {
        channel.offInputReport(listener)
        listener = undefined
      }
      resolveWait?.(result)
    }

    const wait = new Promise<boolean>((resolve) => {
      resolveWait = resolve

      listener = (reportId, data) => {
        const event = parseVisionApDonglePairingReport(reportId, data)
        if (!event) return
        if (event.status === 'enter') {
          console.info('[Telink3228CMousePairing] 接收器进入配对窗', {
            reportId: `0x${VISION_AP_MOUSE_HID.consumerReportId.toString(16)}`,
          })
          return
        }
        if (event.status === 'success') {
          finish(true)
          return
        }
        if (event.status === 'overtime') {
          finish(false)
        }
      }

      channel.onInputReport(listener)
      timer = setTimeout(() => {
        console.warn('[Telink3228CMousePairing] 配对超时：未收到成功/超时上报')
        finish(false)
      }, Telink3228CMousePairing.pairingTimeoutMs)
    })

    return {
      wait,
      dispose: () => finish(false),
    }
  }
}
